'use client';

import { useCallback, useState } from 'react';

import { css } from '@emotion/react';
import { IconButton, IconButtonProps, TextField } from '@mui/material';
import {
  CrispService,
  ImageUsage,
  RouteLink,
  useAmplifyServiceContext,
  useEventAddComment,
  useEventDeletePost,
  useTranslation,
  useUploadImage,
} from '@scorenco/core';
import { useSnackbar } from 'notistack';

import { IconArrowDown } from '../../../icons';
import {
  DropDownMenu,
  DropDownMenuItem,
  DropDownProvider,
  useDropDownTriggerProps,
} from '../../../primitives';
import { T_KEYS } from '../../../translations';

const EventPostCommentItemMenuTrigger = (props: IconButtonProps) => {
  const triggerProps = useDropDownTriggerProps();

  return (
    <IconButton {...triggerProps} {...props} size="small">
      <IconArrowDown />
    </IconButton>
  );
};

export const EventPostCommentItemMenu = ({
  post,
  error,
  setError,
  eventId,
  setEditing,
  ...rest
}: {
  post: any;
  error: boolean;
  setError: (arg: boolean) => void;
  eventId?: number;
  setEditing: (arg: boolean) => void;
}) => {
  const { t } = useTranslation();
  const deletePost = useEventDeletePost();
  const addComment = useEventAddComment();
  const { uploadCapturedImage } = useUploadImage(ImageUsage.LivePostPicture);
  const { user } = useAmplifyServiceContext();
  const { enqueueSnackbar } = useSnackbar();
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState<string>('');

  const reportCrisp = useCallback(() => {
    CrispService.reportComment({
      post: post!,
      url: process.env.NEXT_PUBLIC_MOBILE_URL + '/events/' + eventId,
      reason: reportReason,
    });
  }, [post, eventId, reportReason]);

  return (
    <DropDownProvider>
      <EventPostCommentItemMenuTrigger {...rest} />
      <DropDownMenu>
        {/* <DropDownMenuItem>{t(T_KEYS.EVENT_POST_COMMENT_MODIFY)}</DropDownMenuItem> */}
        {user?.info?.is_superuser && (
          <RouteLink
            href={`https://back.scorenco.com/users/admin/users/user/?id=${post?.spectator?.user_id}`}
          >
            <a target="_blank">
              <DropDownMenuItem>
                Bloquer l'utilisateur ({post?.spectator?.name})
              </DropDownMenuItem>
            </a>
          </RouteLink>
        )}
        {error ? (
          <DropDownMenuItem
            onClick={async () => {
              try {
                setError(false);
                const image = post?.picture
                  ? await uploadCapturedImage(post?.picture)
                  : '';
                await addComment.execute({
                  liveId: post?.live_id,
                  data: post?.data,
                  picture: image,
                });
              } catch {
                setError(true);
              }
            }}
          >
            {t(T_KEYS.EVENT_POST_COMMENT_RETRY)}
          </DropDownMenuItem>
        ) : (
          post?.id !== -1 &&
          (!showReport ? (
            <>
              {(post?.spectator?.user_id === user?.info?.id ||
                user?.info?.is_superuser) && (
                <DropDownMenuItem
                  onClick={() => {
                    setEditing(true);
                  }}
                >
                  Modifier
                </DropDownMenuItem>
              )}
              <DropDownMenuItem
                closeOnClick={false}
                onClick={() => {
                  setShowReport(true);
                }}
              >
                Signaler
              </DropDownMenuItem>
              {/* <DropDownMenuItem>{t(T_KEYS.EVENT_POST_COMMENT_MODIFY)}</DropDownMenuItem> */}
              {(user?.info?.is_superuser ||
                post?.spectator?.user_id == user?.info?.id) && (
                <DropDownMenuItem
                  onClick={() => {
                    deletePost.call({
                      variables: { postId: post.id },
                    });
                  }}
                >
                  {t(T_KEYS.EVENT_POST_ACTION_DELETE)}
                </DropDownMenuItem>
              )}
            </>
          ) : reportReason == '' ? (
            <>
              <DropDownMenuItem
                closeOnClick={false}
                onClick={() => {
                  setReportReason('Score ou action incorrect(e)');
                }}
              >
                Score ou action incorrect(e)
              </DropDownMenuItem>
              <DropDownMenuItem
                closeOnClick={false}
                onClick={() => {
                  setReportReason('Commentaire inapproprié');
                }}
              >
                Commentaire inapproprié
              </DropDownMenuItem>
              <DropDownMenuItem
                closeOnClick={false}
                onClick={() => {
                  setReportReason('Spam');
                }}
              >
                Spam
              </DropDownMenuItem>
              <DropDownMenuItem
                closeOnClick={false}
                onClick={() => {
                  setReportReason('Autre : ');
                }}
              >
                Autre
              </DropDownMenuItem>
            </>
          ) : (
            <>
              {reportReason.startsWith('Autre : ') && (
                <DropDownMenuItem closeOnClick={false}>
                  <TextField
                    label="Raison"
                    variant="outlined"
                    value={reportReason}
                    onChange={(e) =>
                      setReportReason(
                        'Autre : ' + e.target.value.replace('Autre : ', '')
                      )
                    }
                  />
                </DropDownMenuItem>
              )}
              <DropDownMenuItem
                css={css`
                  @media screen and (max-width: 768px) {
                    white-space: normal;
                  }
                `}
              >
                Confirmez-vous que ce texte, cette action ou image soit
                problématique ?
              </DropDownMenuItem>
              <DropDownMenuItem
                onClick={async () => {
                  try {
                    await reportCrisp();
                    enqueueSnackbar('Signalement envoyé', {
                      variant: 'success',
                    });
                    setShowReport(false);
                    setReportReason('');
                  } catch (_e) {
                    enqueueSnackbar("Erreur lors de l'envoi du signalement", {
                      variant: 'error',
                    });
                  }
                }}
                disabled={
                  reportReason == 'Autre : ' && reportReason.length < 15
                }
                css={(theme) => css`
                  color: ${theme.palette.success.main};
                  font-weight: bold;
                `}
              >
                Confirmer
              </DropDownMenuItem>
              <DropDownMenuItem
                onClick={() => {
                  setShowReport(false);
                  setReportReason('');
                }}
                css={(theme) => css`
                  color: ${theme.palette.error.main};
                `}
              >
                Annuler
              </DropDownMenuItem>
            </>
          ))
        )}
      </DropDownMenu>
    </DropDownProvider>
  );
};
